import React from "react";
import { Table, Button, Select, InputNumber, Alert, Tag } from "antd";
import { ChannelMode } from "../../../flatbuffers/generated/archive/channel-mode";
import { ControlFanOut } from "../../../flatbuffers/generated/archive/control-fan-out";
import { ControlWriteMode } from "../../../flatbuffers/generated/archive/control-write-mode";
import { Control } from "../../../flatbuffers/generated/control/control";

/**
 * One Control-message write routing rule, as edited in the wizard.
 *
 * A MERGE/MIXED channel aggregates readings from several endpoints and
 * `merge_policy` reconciles them. Writes had no equivalent: a Control command is
 * addressed to a channel, so with several live endpoints nothing said which one
 * the write should reach.
 */
export interface ControlRouteRow {
  key: string;
  control: Control;
  fanOut: ControlFanOut;
  precedence: number;
  /** Endpoint IDs in fallback order; index 0 is tried first under SINGLE. */
  targetEndpointIds: string[];
}

/** One selectable endpoint of the channel being edited. */
export interface RoutableEndpoint {
  id: string;
  label: string;
  /** False for a read-only leg; such an endpoint can never receive a write. */
  controlWritable: boolean;
}

interface ControlRouteTableProps {
  mode: ChannelMode;
  writeMode: ControlWriteMode;
  onWriteModeChange: (mode: ControlWriteMode) => void;
  rows: ControlRouteRow[];
  onChange: (rows: ControlRouteRow[]) => void;
  endpoints: RoutableEndpoint[];
}

/**
 * Every Control the schema defines, as `{value,label}` pairs.
 *
 * Derived from the generated enum rather than hand-listed: `Control` is
 * append-only and its ordinal IS its wire value, so a hand-written list would
 * silently omit any command added to the schema later. The reverse-mapping
 * entries TypeScript emits for numeric enums are filtered out.
 */
export const CONTROL_OPTIONS: { value: number; label: string }[] = Object.entries(Control)
  .filter(([, value]) => typeof value === "number")
  .map(([label, value]) => ({ value: value as number, label }))
  .sort((a, b) => a.label.localeCompare(b.label));

const WRITE_MODE_OPTIONS = [
  {
    value: ControlWriteMode.ACTIVE_ENDPOINT,
    label: "Active endpoint",
    hint: "Where the write went before routing existed. Safe default.",
  },
  {
    value: ControlWriteMode.PRIMARY_ENDPOINT,
    label: "Primary endpoint",
    hint: "Lowest group/priority endpoint, whether or not it is the active one.",
  },
  {
    value: ControlWriteMode.DROP,
    label: "Drop",
    hint: "Write nowhere and log. Use when an unrouted command must never be sent.",
  },
];

/**
 * Validates the rule set, returning the first problem or null.
 *
 * Mirrors the checks LiftCloud enforces on write, so an operator sees the
 * problem while editing rather than as a rejected save.
 */
export function validateControlRoutes(
  rows: ControlRouteRow[],
  endpoints: RoutableEndpoint[],
): string | null {
  const writable = new Set(endpoints.filter((e) => e.controlWritable).map((e) => e.id));

  const duplicate = rows.find(
    (row, index) =>
      rows.findIndex(
        (other) => other.control === row.control && other.precedence === row.precedence,
      ) !== index,
  );
  if (duplicate !== undefined) {
    return "Two rules share the same command and precedence. Give one a different precedence.";
  }

  if (rows.some((row) => row.targetEndpointIds.length === 0)) {
    return "Every rule needs at least one target endpoint.";
  }

  if (rows.some((row) => new Set(row.targetEndpointIds).size !== row.targetEndpointIds.length)) {
    return "A rule lists the same endpoint twice.";
  }

  const unwritable = rows.flatMap((row) => row.targetEndpointIds).find((id) => !writable.has(id));
  if (unwritable !== undefined) {
    const label = endpoints.find((e) => e.id === unwritable)?.label ?? unwritable;
    return `Endpoint "${label}" is not control-writable and cannot be a target.`;
  }

  return null;
}

export default function ControlRouteTable({
  mode,
  writeMode,
  onWriteModeChange,
  rows,
  onChange,
  endpoints,
}: ControlRouteTableProps) {
  // SINGLE and FAILOVER channels have exactly one live connection, so there is
  // nothing to route between.
  if (mode !== ChannelMode.MERGE && mode !== ChannelMode.MIXED) {
    return null;
  }

  const validationError = validateControlRoutes(rows, endpoints);
  const writableEndpoints = endpoints.filter((e) => e.controlWritable);

  const updateRow = (index: number, patch: Partial<ControlRouteRow>) => {
    onChange(rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  const handleAddRule = () => {
    const usedControls = new Set(rows.map((r) => r.control));
    const nextControl =
      CONTROL_OPTIONS.find((o) => !usedControls.has(o.value as Control))?.value ??
      CONTROL_OPTIONS[0].value;

    onChange([
      ...rows,
      {
        key: crypto.randomUUID(),
        control: nextControl as Control,
        fanOut: ControlFanOut.SINGLE,
        precedence: 0,
        targetEndpointIds: writableEndpoints.length > 0 ? [writableEndpoints[0].id] : [],
      },
    ]);
  };

  const handleRemoveRule = (index: number) => {
    onChange(rows.filter((_, i) => i !== index));
  };

  const columns = [
    {
      title: "Command",
      dataIndex: "control",
      key: "control",
      render: (_: number, record: ControlRouteRow, index: number) => (
        <Select
          data-testid={`control-route-command-${index}`}
          showSearch
          optionFilterProp="label"
          style={{ width: 220 }}
          value={record.control}
          options={CONTROL_OPTIONS}
          onChange={(value) => updateRow(index, { control: value as Control })}
        />
      ),
    },
    {
      title: "Goes to",
      dataIndex: "targetEndpointIds",
      key: "targetEndpointIds",
      render: (_: string[], record: ControlRouteRow, index: number) => (
        <Select
          data-testid={`control-route-targets-${index}`}
          mode="multiple"
          style={{ width: 260 }}
          placeholder="Select endpoint(s)"
          value={record.targetEndpointIds}
          options={writableEndpoints.map((e) => ({ value: e.id, label: e.label }))}
          onChange={(value) => updateRow(index, { targetEndpointIds: value as string[] })}
        />
      ),
    },
    {
      title: "Fan-out",
      dataIndex: "fanOut",
      key: "fanOut",
      render: (_: number, record: ControlRouteRow, index: number) => (
        <Select
          data-testid={`control-route-fanout-${index}`}
          style={{ width: 190 }}
          value={record.fanOut}
          options={[
            { value: ControlFanOut.SINGLE, label: "First reachable" },
            { value: ControlFanOut.ALL, label: "All targets" },
          ]}
          onChange={(value) => updateRow(index, { fanOut: value as ControlFanOut })}
        />
      ),
    },
    {
      title: "Precedence",
      dataIndex: "precedence",
      key: "precedence",
      render: (_: number, record: ControlRouteRow, index: number) => (
        <InputNumber
          min={0}
          value={record.precedence}
          onChange={(value) => updateRow(index, { precedence: value ?? 0 })}
          style={{ width: 90 }}
        />
      ),
    },
    {
      title: "",
      key: "actions",
      render: (_: unknown, __: ControlRouteRow, index: number) => (
        <Button
          danger
          size="small"
          data-testid={`control-route-remove-${index}`}
          onClick={() => handleRemoveRule(index)}
        >
          Remove
        </Button>
      ),
    },
  ];

  const broadcastsAnything = rows.some((r) => r.fanOut === ControlFanOut.ALL);

  return (
    <div data-testid="control-route-table">
      <h4 style={{ marginBottom: 4 }}>Control write routing</h4>
      <p style={{ fontSize: 12, opacity: 0.75, marginBottom: 12 }}>
        This channel reads from several endpoints. These rules decide which endpoint each command is{" "}
        <em>written</em> to. Commands with no rule use the fallback below.
      </p>

      <div style={{ marginBottom: 12 }}>
        <span style={{ marginRight: 8 }}>Unrouted commands:</span>
        <Select
          data-testid="control-write-mode"
          style={{ width: 220 }}
          value={writeMode}
          options={WRITE_MODE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
          onChange={(value) => onWriteModeChange(value as ControlWriteMode)}
        />
        <span style={{ fontSize: 12, opacity: 0.7, marginLeft: 8 }}>
          {WRITE_MODE_OPTIONS.find((o) => o.value === writeMode)?.hint}
        </span>
      </div>

      {validationError !== null && (
        <Alert
          type="error"
          showIcon
          message={validationError}
          style={{ marginBottom: 12 }}
          data-testid="control-route-validation-error"
        />
      )}

      {broadcastsAnything && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          data-testid="control-route-fanout-warning"
          message="One or more rules write to every target"
          description={
            "A command sent to several endpoints actuates on each of them. That is correct " +
            "for locks and secures, but a physical command such as OpenDoor or RunUp will " +
            "act twice."
          }
        />
      )}

      <Table
        size="small"
        rowKey="key"
        pagination={false}
        columns={columns}
        dataSource={rows}
        locale={{ emptyText: "No rules — every command uses the fallback above." }}
      />

      <Button
        style={{ marginTop: 12 }}
        onClick={handleAddRule}
        data-testid="control-route-add"
        disabled={writableEndpoints.length === 0}
      >
        Add rule
      </Button>

      {writableEndpoints.length === 0 && (
        <Tag color="warning" style={{ marginLeft: 8 }}>
          No control-writable endpoints on this channel
        </Tag>
      )}
    </div>
  );
}
